import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { CACHE_TIMES } from '../lib/query-client'

// React Query-backed Stripe Connect account status. One fetch, dedup'd
// across every caller (settings index pill, Stripe detail page, dashboard
// notice, subscription page, status monitor). Replaces a hand-rolled hook
// where each consumer mounted independently and refetched on every page
// navigation.
//
// Key: ['stripeAccountStatus', companyId]
//
// Stale time matches other Settings-scope queries — five minutes is plenty
// for a status pill; tab focus refetch handles the "user just came back
// from the Stripe dashboard" case.

const fetchStripeAccountStatus = async (companyId) => {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-get-account-status`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ companyId }),
    },
  )

  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch account status')
  }
  return data.account ?? null
}

export const useStripeAccountStatus = (companyId, options = {}) => {
  const { enabled = true } = options

  const query = useQuery({
    queryKey: ['stripeAccountStatus', companyId],
    queryFn: () => fetchStripeAccountStatus(companyId),
    enabled: enabled && !!companyId,
    staleTime: CACHE_TIMES.ESSENTIAL_STALE,
    gcTime: CACHE_TIMES.GC_TIME,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  const accountStatus = query.data ?? null

  return useMemo(() => {
    const requirements = accountStatus?.requirements || {}
    const capabilities = accountStatus?.capabilities || {}
    const cardPaymentsStatus = capabilities.card_payments || null

    const hasCurrentlyDueRequirements =
      Array.isArray(requirements.currently_due) && requirements.currently_due.length > 0
    const hasPastDueRequirements =
      Array.isArray(requirements.past_due) && requirements.past_due.length > 0
    const hasEventuallyDueRequirements =
      Array.isArray(requirements.eventually_due) && requirements.eventually_due.length > 0

    const isCardPaymentsRestricted =
      cardPaymentsStatus === 'restricted' || cardPaymentsStatus === 'pending'

    return {
      accountStatus,
      loading: query.isLoading,
      error: query.error ? query.error.message : null,
      isReadyForPayments: accountStatus?.charges_enabled === true,
      isPendingApproval:
        !!accountStatus && !accountStatus.charges_enabled && accountStatus.details_submitted,
      needsOnboarding: !!accountStatus && !accountStatus.details_submitted,
      isPayoutsDisabled:
        accountStatus?.charges_enabled === true && accountStatus?.payouts_enabled === false,
      hasRequirements:
        hasCurrentlyDueRequirements || hasPastDueRequirements || hasEventuallyDueRequirements,
      hasCurrentlyDueRequirements,
      hasPastDueRequirements,
      hasEventuallyDueRequirements,
      requirements,
      cardPaymentsStatus,
      isCardPaymentsRestricted,
      isCardPaymentsActive: cardPaymentsStatus === 'active',
      isRestricted:
        accountStatus?.charges_enabled === true &&
        (isCardPaymentsRestricted || hasPastDueRequirements),
      refetch: query.refetch,
    }
  }, [accountStatus, query.isLoading, query.error, query.refetch])
}
